import { inject, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterModule,
  Routes,
} from '@angular/router';
import { RouterStateSerializer } from '@ngrx/router-store';
import { canActivateAuthGuard } from '@okta/okta-angular';

/* Components */
import * as fromBillingContainers from 'src/app/billing/pages';
import * as fromServiceInventoryComponents from 'src/app/inventory/components';
import * as fromServiceInventoryContainers from 'src/app/inventory/containers';
import * as UnusualNetworkMetricsContainers from 'src/app/iq-analytics/unusual-network-metrics/pages';
import { OrdersPageComponent } from 'src/app/orders/pages/orders-page/orders-page.component';
import { OverallNetworkStatusPageComponent } from 'src/app/overall-network-status/containers';
import * as fromGuards from 'src/app/store/guards';
import * as fromTicketContainers from 'src/app/tickets/containers';
import { AppLayoutComponent } from './_layout/app-layout/app-layout.component';
import { PublicLayoutComponent } from './_layout/public-layout/public-layout.component';
import { AccessDeniedComponent } from './core/access-denied/access-denied.component';
import { AccessUnauthorizedComponent } from './core/access-unauthorized/access-unauthorized.component';
import { ForgotPasswordComponent } from './core/forgot-password/forgot-password.component';
import { ImpersonationComponent } from './core/impersonate/impersonation.component';
import { LoginComponent } from './core/login/login.component';
import { LogoutComponent } from './core/logout/logout.component';
import { OktaCallbackCustomComponent } from './core/okta-callback/okta-callback.component';
import { OktaMigrationComponent } from './core/okta-migration/okta-migration.component';
import { PageNotFoundComponent } from './core/page-not-found/page-not-found.component';
import { RegisterComponent } from './core/register/register.component';
import { ResetPasswordComponent } from './core/reset-password/reset-password.component';
import { P95UtilizationPageComponent } from './inventory/p95-utilization/containers';
import { IqDashboardComponent } from './iq-analytics/aggregated-dashboard/pages';
import { MetricTableComponent } from './iq-analytics/metric-details/pages';
import { QoePageComponent } from './iq-analytics/quality-of-experience/pages';
import { ManageUsersComponent } from './manage-users/manage-users.component';
import { OktaAdminComponent } from './manage-users/okta-admin/okta-admin.component';
import { NocPolicyComponent } from './noc-policy/noc-policy.component';
import { OrderInsightsPageComponent } from './orders/pages/order-insights/order-insights-page.component';
import { CustomSerializer } from './store/reducers/router.reducer';
import { TicketInsightsComponent } from './tickets/containers/ticket-insights/ticket-insights.component';

import { Permission } from './shared/constants/permissions';

const redirectOrdersTableToOrders: CanActivateFn = (route) =>
  inject(Router).createUrlTree(['/orders'], {
    queryParams: { ...route.queryParams, view: 'table' },
  });

const redirectOrdersTableDetailToOrders: CanActivateFn = (route) =>
  inject(Router).createUrlTree(['/orders', route.params.orderId], {
    queryParams: { ...route.queryParams, view: 'table' },
  });

const ROUTES: Routes = [
  {
    path: '',
    component: AppLayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'migrate',
        pathMatch: 'full',
      },
      {
        path: 'overall-network-status',
        redirectTo: 'analytics/overall-network-status',
        pathMatch: 'full',
      },
      {
        path: 'analytics/overall-network-status',
        title: 'Overall Network Status | MyPortal',
        component: OverallNetworkStatusPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.InvoiceAttachmentsGuard,
          fromGuards.PaymentsGuard,
        ],
      },

      {
        path: 'unusual-network-metrics',
        redirectTo: 'analytics/unusual-network-metrics',
        pathMatch: 'full',
      },
      {
        path: 'analytics/unusual-network-metrics',
        title: 'Unusual Network Metrics | MyPortal',
        component: UnusualNetworkMetricsContainers.UnmPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },

      {
        path: 'summary',
        redirectTo: 'orders/insights',
        pathMatch: 'full',
      },
      {
        path: 'orders-dashboard',
        redirectTo: 'orders/insights',
        pathMatch: 'full',
      },
      {
        path: 'repors/orders-summary',
        redirectTo: 'orders/insights',
        pathMatch: 'full',
      },
      {
        path: 'orders/insights',
        title: 'Orders Insights | MyPortal',
        component: OrderInsightsPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'services/p95-utilization',
        title: '95th Percentile Bandwidth Utilization | MyPortal',
        component: P95UtilizationPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.SERVICES, Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'operations-summary',
        redirectTo: 'tickets/insights',
        pathMatch: 'full',
      },
      {
        path: 'repors/operations-summary',
        redirectTo: 'tickets/insights',
        pathMatch: 'full',
      },
      {
        path: 'tickets/insights',
        title: 'Ticket Insights | MyPortal',
        component: TicketInsightsComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.TICKETING_READ],
          }),
        ],
      },
      {
        path: 'billing',
        title: 'Billing | MyPortal',
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PaymentsGuard,
          fromGuards.InvoicesGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.BILLING_READ],
          }),
        ],
        children: [
          { path: '', component: fromBillingContainers.BillingPageComponent },
        ],
      },
      {
        path: 'metric-details',
        redirectTo: 'analytics/metric-details',
        pathMatch: 'full',
      },
      {
        path: 'analytics/metric-details',
        title: 'Metric Details | MyPortal',
        component: MetricTableComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'quality-of-experience',
        redirectTo: 'analytics/quality-of-experience',
        pathMatch: 'full',
      },
      {
        path: 'quality-of-experience-aggregate',
        redirectTo: 'analytics/quality-of-experience',
        pathMatch: 'full',
      },
      {
        path: 'analytics/quality-of-experience',
        title: 'Quality of Experience | MyPortal',
        component: QoePageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'services/:serviceId',
        title: 'Service Details | MyPortal',
        component: fromServiceInventoryComponents.InventoryDetailComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.ServicesGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.SERVICES],
          }),
        ],
      },
      {
        path: 'services',
        title: 'Services | MyPortal',
        component: fromServiceInventoryContainers.InventoryPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.SERVICES],
          }),
        ],
      },
      {
        path: 'orders/:orderId/line-items/:lineItemId',
        title: 'Order Details | MyPortal',
        component: OrdersPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.OrdersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'orders/:orderId',
        title: 'Order Details | MyPortal',
        component: OrdersPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.OrdersGuard,
          fromGuards.SOFGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'orders',
        title: 'Orders | MyPortal',
        component: OrdersPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'orders-table',
        title: 'Orders Table | MyPortal',
        component: OrdersPageComponent,
        canActivate: [redirectOrdersTableToOrders],
      },
      {
        path: 'orders-table/:orderId/view',
        title: 'Order Details | MyPortal',
        component: OrdersPageComponent,
        canActivate: [redirectOrdersTableDetailToOrders],
      },
      {
        path: 'tickets/:ticketId',
        title: 'Ticket Details | MyPortal',
        component: fromTicketContainers.TicketsDetailShellComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.TicketsGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.TICKETING_READ],
          }),
        ],
      },
      {
        path: 'tickets',
        title: 'Tickets | MyPortal',
        component: fromTicketContainers.TicketsShellComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.TICKETING_READ],
          }),
        ],
      },
      {
        path: 'analytics/overview',
        title: 'IQ Analytics Overview | MyPortal',
        component: IqDashboardComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'analytics/historical-observations',
        title: 'Historical Observations | MyPortal',
        loadChildren: () =>
          import('./iq-analytics/historical-observations/historical-observations.module').then(
            (m) => m.HistoricalObservationsModule,
          ),
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'dashboard',
        title: 'Dashboard | MyPortal',
        component: OverallNetworkStatusPageComponent,
        canActivate: [fromGuards.DynamicAuthGuard, fromGuards.UsersGuard],
      },
      {
        path: 'migrate',
        title: 'Migrate Account | MyPortal',
        component: OktaMigrationComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.OktaMigrationGuard,
        ],
      },
      {
        path: 'noc-policy',
        title: 'NOC Policy | MyPortal',
        component: NocPolicyComponent,
        canActivate: [fromGuards.DynamicAuthGuard, fromGuards.UsersGuard],
      },
      {
        path: 'manage/account',
        title: 'Manage Account | MyPortal',
        component: ManageUsersComponent,
        canActivate: [canActivateAuthGuard, fromGuards.UsersGuard],
      },
      {
        path: 'okta-admin',
        title: 'Admin Dashboard | MyPortal',
        component: OktaAdminComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_INTERNAL],
          }),
        ],
      },
      {
        path: 'access-denied',
        title: 'Access Denied | MyPortal',
        component: AccessDeniedComponent,
      },
      {
        path: 'unauthorized',
        title: 'Unauthorized | MyPortal',
        component: AccessUnauthorizedComponent,
      },
    ],
  },
  {
    // Logged out routes go here
    path: '',
    component: PublicLayoutComponent,
    children: [
      {
        path: 'login/callback',
        title: 'Login | MyPortal',
        component: OktaCallbackCustomComponent,
      },
      {
        path: 'page-not-found',
        title: 'Page Not Found | MyPortal',
        component: PageNotFoundComponent,
      },
      {
        path: 'logout',
        title: 'Logout | MyPortal',
        component: LogoutComponent,
      },
      {
        path: 'login',
        title: 'Login | MyPortal',
        component: LoginComponent,
        canActivate: [fromGuards.AlreadyAuthenticatedGuard],
      },
      {
        path: 'forgot-password',
        title: 'Forgot Password | MyPortal',
        component: ForgotPasswordComponent,
      },
      {
        path: 'register',
        title: 'Register | MyPortal',
        component: RegisterComponent,
        canActivate: [fromGuards.AlreadyAuthenticatedGuard],
      },
      {
        path: 'reset-password',
        title: 'Reset Password | MyPortal',
        component: ResetPasswordComponent,
      },
      {
        path: 'signin_as',
        title: 'Impersonation | MyPortal',
        component: ImpersonationComponent,
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      { path: '**', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(ROUTES, {
      anchorScrolling: 'enabled',
      scrollOffset: [0, 70],
      enableTracing: false,
    }),
  ],
  exports: [RouterModule],
  providers: [
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    {
      provide: 'externalUrlRedirectResolver',
      useValue: (route: ActivatedRouteSnapshot) => {
        window.location.href = (route.data as any).externalUrl;
      },
    },
  ],
})
export class AppRoutingModule {}
